import React, { useRef, useState, useEffect } from "react";
import { Editor } from "@tinymce/tinymce-react";
import { toast } from "react-toastify";

const fontWeights = [
  // { text: "Thin 100", value: "100" },
  // { text: "Thin 100 Italic", value: "100 italic" },
  // { text: "ExtraLight 200", value: "200" },
  // { text: "ExtraLight 200 Italic", value: "200 italic" },
  // { text: "Light 300", value: "300" },
  { text: "Regular", value: "400" },
  { text: "Regular Italic", value: "400 italic" },
  // { text: "Medium 500", value: "500" },
  // { text: "Medium 500 Italic", value: "500 italic" },
  // { text: "SemiBold 600", value: "600" },
  { text: "Bold", value: "800" },
  { text: "Bold Italic", value: "800 italic" },
  // { text: "Black 900", value: "900" },
];

const ContentEditor = ({
  value,
  handleUpdateElement,
  placeholder,
  isdisabled,
}) => {
  const editorRef = useRef(null);
  const [loading, setLoading] = useState(true);

  // Helper function to clean content
  const cleanContent = (content) => {
    if (!content) return "";

    let cleaned = content.trim();

    // Remove empty paragraphs with nbsp or <br>
    cleaned = cleaned.replace(/^(<p>(&nbsp;|\s|<br\s*\/?>)*<\/p>)+/gi, "");
    cleaned = cleaned.replace(/(<p>(&nbsp;|\s|<br\s*\/?>)*<\/p>)+$/gi, "");

    // Remove any <p></p> directly before a <ul> or <ol>
    cleaned = cleaned.replace(/<p>\s*<\/p>(\s*<ul|<ol)/gi, "$1");

    // Remove leading/trailing &nbsp;
    cleaned = cleaned.replace(/^(&nbsp;|\s)+/, "");
    cleaned = cleaned.replace(/(&nbsp;|\s)+$/, "");

    // Remove empty standalone <p></p>
    cleaned = cleaned.replace(/^<p><\/p>/, "");
    cleaned = cleaned.replace(/<p><\/p>$/, "");

    return cleaned;
  };

  const handleEditorInit = (evt, editor) => {
    editorRef.current = editor;
    setLoading(false);
  };

  useEffect(() => {
    if (editorRef.current && value !== editorRef.current.getContent()) {
      // Clean the value before setting it
      const cleanedValue = cleanContent(value || "");
      editorRef.current.setContent(cleanedValue);
    }
  }, [value]);

  const getCurrentLi = (editor) => editor.selection.getNode().closest("li");

  const ensureTextNode = (li, type) => {
    if (!li.firstChild || li.firstChild.nodeType !== Node.TEXT_NODE) {
      li.textContent = type === "hyphen" ? "- " : "";
    }
    return li.firstChild;
  };

  const wrapSelectionInLi = (editor, type) => {
    const content = editor.selection.getContent({ format: "text" }) || "";
    const lines = content.split(/\r?\n/).filter((line) => line.trim() !== "");

    const ul = document.createElement("ul");

    lines.forEach((line) => {
      const li = document.createElement("li");
      li.style.listStyleType = type === "bullet" ? "disc" : "none";
      const text =
        type === "hyphen" && !line.startsWith("- ") ? "- " + line : line;
      const textNode = document.createTextNode(text);
      li.appendChild(textNode);
      ul.appendChild(li);
    });

    if (lines.length === 0) {
      const li = document.createElement("li");
      li.style.listStyleType = type === "bullet" ? "disc" : "none";
      // Add a zero-width space to make it count as content
      const textNode = document.createTextNode(
        type === "hyphen" ? "- \u200B" : "\u200B"
      );
      li.appendChild(textNode);
      ul.appendChild(li);
    }

    editor.selection.setContent(ul.outerHTML);

    // Trigger change event to hide placeholder
    editor.fire("change");
    editor.fire("input");

    const firstLi = ul.querySelector("li");
    if (firstLi && firstLi.firstChild) {
      const range = document.createRange();
      range.setStart(firstLi.firstChild, type === "hyphen" ? 2 : 0);
      range.collapse(true);
      const sel = editor.selection.getSel();
      sel.removeAllRanges();
      sel.addRange(range);
    }

    editor.focus();
  };

  return (
    <div className="text-editor">
      {loading && <div className="loader">Loading editor...</div>}
      <Editor
        key={isdisabled ? "disabled" : "enabled"}
        apiKey="gpl"
        tinymceScriptSrc={window.location.origin + "/tinymce/tinymce.min.js"}
        onInit={handleEditorInit}
        value={typeof value === "string" ? value : ""}
        init={{
          height: 400,
          menubar: false,
          branding: false,
          placeholder,
          plugins: "lists link autolink",
          toolbar: "bulletToggle hyphenToggle | fontWeightSelect | link",
          readonly: isdisabled,
          highlight_on_focus: false,
          forced_root_block: false,
          force_br_newlines: true,
          force_p_newlines: false,
          paste_as_text: true,
          entity_encoding: "raw",
          remove_trailing_brs: true,
          verify_html: false,
          cleanup: true,
          trim_span_elements: true,
          remove_redundant_brs: true,
          pad_empty_with_br: false,
          paste_preprocess: (plugin, args) => {
            const htmlRegex = /<(?!(br\s*\/?>))[^>]*>|&lt;.*?&gt;/i;
            if (htmlRegex.test(args.content)) {
              toast.warning(
                "HTML is not allowed in this field (except line breaks)."
              );
              args.preventDefault();
              args.content = "";
            }
          },
          content_style: `
                @import url('https://fonts.googleapis.com/css2?family=Roboto:wght@400&display=swap');
                body {
                  color: #0066BE;
                  font-family: 'Roboto', Arial, sans-serif;
                  font-weight: 400;
                  font-size: 16px;
                  pointer-events: auto !important;
                  caret-color: ${isdisabled ? "transparent" : "#000"};
                  cursor: ${isdisabled ? "default" : "text"};
                }
                a {
                  color: #8625a1;
                  text-decoration: underline;
                }
                li.hyphen {
                  list-style-type: none;
                  position: relative;
                  margin-top: 5px;  
                  padding-left: 1.1em;
                  text-indent: -1.0em;
                }
                li.hyphen::before {
                  position: absolute;
                  left: 0;
                }
              `,
          setup: (editor) => {
            // Clean content when getting it from editor
            editor.on("GetContent", function (e) {
              e.content = cleanContent(e.content);
            });
            // Clean content on blur (when user leaves editor)
            editor.on("blur", function () {
              const content = editor.getContent();
              const cleaned = cleanContent(content);
              if (content !== cleaned) {
                editor.setContent(cleaned);
              }
            });

            // Font Weight Dropdown
            let currentFontWeight = "Font Weight";
            if (isdisabled) {
              // Prevent typing, pasting, selecting
              editor.on("keydown", (e) => e.preventDefault());
              editor.on("paste", (e) => e.preventDefault());
              editor.on("cut", (e) => e.preventDefault());
              editor.on("drop", (e) => e.preventDefault());
              editor.on("click", () => editor.selection.collapse());
              editor.on("focus", () => editor.selection.collapse());
              editor.on("mouseup", () => editor.selection.collapse());

              // Prevent the link command from executing
              editor.on("BeforeExecCommand", (e) => {
                if (e.command === "mceLink") {
                  e.preventDefault();

                  // Make the link button visually inactive
                  const linkBtn = editor
                    .getContainer()
                    .querySelector(".tox-tbtn[title='Insert/edit link']");
                  if (linkBtn) {
                    linkBtn.classList.remove("tox-tbtn--active");
                  }
                }
              });

              // Also force inactive whenever selection changes
              editor.on("init", () => {
                if (isdisabled) {
                  // Disable toolbar buttons visually
                  const toolbarBtns = editor
                    .getContainer()
                    .querySelectorAll(".tox-tbtn");
                  toolbarBtns.forEach((btn) => {
                    btn.style.pointerEvents = "none";
                    btn.style.opacity = "0.6";
                  });

                  // Inject dynamic CSS to remove hover background
                  const style = document.createElement("style");
                  style.innerHTML = `
                  .tox .tox-tbtn:hover {
                    background-color: transparent !important;
                    box-shadow: none !important;
                    cursor: ${isdisabled ? "not-allowed" : "text"};
                  }
                `;
                  editor.getContainer().appendChild(style);
                }
              });
            }

            editor.ui.registry.addSplitButton("fontWeightSelect", {
              text: currentFontWeight,
              onAction: () => {
                // Optional: toggle through weights if clicked directly (not the dropdown)
              },
              fetch: (callback) => {
                const items = fontWeights.map((fw) => ({
                  type: "choiceitem",
                  text: fw.text,
                  value: fw.value,
                }));
                callback(items);
              },
              onItemAction: (api, value) => {
                const [weight, style] = value.split(" ");
                editor.formatter.remove("italic");
                if (style === "italic") editor.formatter.apply("italic");
                editor.formatter.apply("customFontWeight", { value: weight });

                // Update button label dynamically
                currentFontWeight =
                  fontWeights.find((fw) => fw.value === value)?.text ||
                  "Font Weight";
                api.setActive(true);
                api.setText(currentFontWeight);
              },
              onSetup: (api) => {
                const handler = () => {
                  if (isdisabled) {
                    api.setActive(false);
                    return;
                  }
                  const node = editor.selection.getNode();
                  const computedWeight =
                    window.getComputedStyle(node).fontWeight;
                  const isItalic =
                    window.getComputedStyle(node).fontStyle === "italic";

                  const match = fontWeights.find(
                    (fw) =>
                      fw.value ===
                      `${computedWeight}${isItalic ? " italic" : ""}`
                  );
                  const label = match ? match.text : "Font Weight";
                  api.setText(label);
                };
                if (isdisabled) api.setEnabled(false);
                editor.on("NodeChange", handler);
                return () => editor.off("NodeChange", handler);
              },
            });

            editor.on("init", () => {
              editor.formatter.register("customFontWeight", {
                inline: "span",
                styles: { "font-weight": "%value" },
              });

              setTimeout(() => {
                const toolbar = editor.getContainer();
                const selectLabels = toolbar.querySelectorAll(
                  ".tox-tbtn__select-label"
                );

                selectLabels.forEach((label) => {
                  const text = label.textContent.trim();
                  if (text === "Bullet" || text === "Hyphen") {
                    label.parentElement.classList.remove("tox-tbtn--select");
                  }
                });
              }, 0);
            });

            // Bullet Toggle
            editor.ui.registry.addToggleButton("bulletToggle", {
              text: "Bullet",
              onAction: (api) => {
                if (isdisabled) return;
                const li = getCurrentLi(editor);
                if (!li) {
                  wrapSelectionInLi(editor, "bullet");
                  return;
                }
                li.style.listStyleType = "disc";
                const textNode = ensureTextNode(li, "bullet");
                if (textNode.data.startsWith("- ")) textNode.deleteData(0, 2);

                const range = document.createRange();
                range.setStart(textNode, 0);
                range.collapse(true);
                const sel = editor.selection.getSel();
                sel.removeAllRanges();
                sel.addRange(range);
                editor.focus();
                // Trigger change event to hide placeholder
                editor.fire("change");
                editor.fire("input");
                api.setActive(true);
              },
              onSetup: (api) => {
                const handler = () => {
                  if (isdisabled) {
                    api.setActive(false);
                    return;
                  }
                  const li = getCurrentLi(editor);
                  api.setActive(li?.style.listStyleType === "disc");
                };
                if (isdisabled) api.setEnabled(false);
                editor.on("NodeChange", handler);
                return () => editor.off("NodeChange", handler);
              },
            });

            // Hyphen Toggle
            editor.ui.registry.addToggleButton("hyphenToggle", {
              text: "Hyphen",
              onAction: (api) => {
                if (isdisabled) return;
                const li = getCurrentLi(editor);
                if (!li) {
                  wrapSelectionInLi(editor, "hyphen");
                  return;
                }
                li.style.listStyleType = "none";
                const isHyphen = li.classList.contains("hyphen");
                // if (isHyphen) {
                //   li.classList.remove("hyphen");
                // } else {
                //   li.classList.add("hyphen");
                // }
                li.classList.add("hyphen");
                const textContent = li.textContent.trim();
                const isEmpty =
                  textContent === "" ||
                  textContent === "-" ||
                  textContent === "- ";
                const textNode = ensureTextNode(li, "hyphen");
                if (!textNode.data.startsWith("- \u00A0\u00A0")) {
                  textNode.insertData(0, "- \u00A0\u00A0");
                  if (isEmpty) textNode.insertData(0, "");
                }
                const range = document.createRange();
                range.setStart(textNode, 3);
                range.collapse(true);
                const sel = editor.selection.getSel();
                sel.removeAllRanges();
                sel.addRange(range);
                editor.focus();
                // Trigger change event to hide placeholder
                editor.fire("change");
                editor.fire("input");
                api.setActive(true);
              },
              onSetup: (api) => {
                const handler = () => {
                  if (isdisabled) {
                    api.setActive(false);
                    return;
                  }
                  const li = getCurrentLi(editor);
                  if (!li) {
                    api.setActive(false);
                    return;
                  }

                  // More robust condition for hyphen
                  const isHyphen =
                    li.style.listStyleType === "none" &&
                    li.textContent.trim().startsWith("-");
                  if (isHyphen && !li.classList.contains("hyphen")) {
                    li.classList.add("hyphen");
                    const textNode = ensureTextNode(li, "hyphen");
                    if (!textNode.data.startsWith("- \u00A0\u00A0"))
                      textNode.insertData(1, "\u00A0\u00A0");
                  }
                  api.setActive(isHyphen);
                };
                if (isdisabled) api.setEnabled(false);
                // Run on init and NodeChange
                editor.on("NodeChange", handler);
                handler(); // set initial state
                return () => editor.off("NodeChange", handler);
              },
            });

            // Custom Enter Key and Backspace Behavior
            editor.on("keydown", (e) => {
              if (e.key === "Enter") {
                const li = getCurrentLi(editor);
                if (!li) return;
                // Check if list item is empty
                const textContent = li.textContent.trim();
                const isEmpty =
                  textContent === "" ||
                  textContent === "-" ||
                  textContent === "- ";
                if (isEmpty) {
                  // Exit the list - remove empty li and add text node after ul
                  e.preventDefault();
                  const ul = li.closest("ul, ol");
                  if (ul) {
                    // Create a text node or paragraph after the list
                    const textNode = document.createTextNode("\u200B"); // zero-width space
                    ul.parentNode.insertBefore(textNode, ul.nextSibling);
                    // Remove the empty li
                    li.remove();
                    // If list is now empty, remove it too
                    if (ul.children.length === 0) {
                      ul.remove();
                    }
                    // Set cursor after the list
                    const range = document.createRange();
                    range.setStart(textNode, 0);
                    range.collapse(true);
                    const sel = editor.selection.getSel();
                    sel.removeAllRanges();
                    sel.addRange(range);
                    editor.focus();
                  }
                  return;
                }

                // Normal behavior: create new list item
                e.preventDefault();
                const liType =
                  li.style.listStyleType === "disc" ? "bullet" : "hyphen";

                const newLi = document.createElement("li");
                newLi.style.listStyleType =
                  liType === "bullet" ? "disc" : "none";
                const textNode = document.createTextNode(
                  liType === "hyphen" ? "- " : ""
                );
                newLi.appendChild(textNode);
                li.parentNode.insertBefore(newLi, li.nextSibling);

                const range = document.createRange();
                range.setStart(textNode, liType === "hyphen" ? 2 : 0);
                range.collapse(true);
                const sel = editor.selection.getSel();
                sel.removeAllRanges();
                sel.addRange(range);
                editor.focus();
              }
              // Backspace at the beginning of a list item exits the list
              if (e.key === "Backspace") {
                const li = getCurrentLi(editor);
                if (!li) return;
                const sel = editor.selection.getSel();
                const range = sel.getRangeAt(0);
                // Check if cursor is at the very beginning of the list item
                if (
                  range.startOffset === 0 &&
                  li.firstChild === range.startContainer
                ) {
                  e.preventDefault();
                  const ul = li.closest("ul, ol");
                  const textContent = li.textContent
                    .replace(/^-\s*/, "")
                    .trim();
                  // Create text node with the content (if any)
                  const isEmpty =
                    li.textContent.trim() === "" ||
                    li.innerHTML === "<br>" ||
                    li.innerHTML === "<br/>";
                  const prev = li.previousElementSibling;
                  // Remove the li
                  li.remove();
                  // If list is now empty, remove it
                  if (ul.children.length === 0) ul.remove();

                  const newRange = document.createRange();
                  const textNode = document.createTextNode(
                    isEmpty && prev ? textContent : textContent || "\u200B"
                  );
                  ul.parentNode.insertBefore(textNode, ul);
                  if (isEmpty && prev)
                    newRange.setStart(prev, prev.childNodes.length);
                  else newRange.setStart(textNode, 0);
                  newRange.collapse(true);
                  sel.removeAllRanges();
                  sel.addRange(newRange);
                  editor.focus();
                }
              }
            });
          },
        }}
        onEditorChange={(content) => {
          const cleaned = cleanContent(content || "");
          handleUpdateElement(cleaned);
        }}
      />
    </div>
  );
};

export default ContentEditor;
